import { useAppDispatch } from "@/store/hooks";
import { updateElement, updateElementProps } from "@/store/slideEditorSlice";
import { resolveCanvasFontFamily } from "@ai-teach/canvas-styles";
import type { TextElement } from "@ai-teach/types";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";

interface TextRendererProps {
  element: TextElement;
  isLiveMode?: boolean;
  isSelected?: boolean;
}

export function TextRenderer({
  element,
  isLiveMode = false,
  isSelected = false,
}: TextRendererProps) {
  const dispatch = useAppDispatch();
  const divRef = useRef<HTMLDivElement>(null);
  const isFocused = useRef(false);
  // First click selects (and drags); a click while already selected opens editing.
  const [isEditing, setIsEditing] = useState(false);
  // Bumped per click so the caret effect re-runs even when editing is already on (click → dbl-click).
  const [caretNonce, setCaretNonce] = useState(0);
  const pointerDownAt = useRef<{ x: number; y: number } | null>(null);
  const editCaretPoint = useRef<{ x: number; y: number } | null>(null);
  const editSelectWord = useRef(false);
  // The element's own authored height the moment editing started — auto-grow can push the box
  // taller than this as lines are added, and auto-shrink can bring it back down as lines are
  // removed, but never below this: it's the floor a manual resize set before this edit began, so
  // an intentionally-oversized box a teacher left extra room in never gets discarded just because
  // they typed something.
  const editStartHeight = useRef(element.size.height);

  useEffect(() => {
    if (!isSelected) setIsEditing(false);
  }, [isSelected]);

  // Auto-grow/shrink the element's persisted height to fit its content while editing — text is
  // never allowed to clip/blank out behind the box's old height, and the box's true footprint is
  // what the teacher sees immediately, not just at render time elsewhere.
  const syncHeightToContent = () => {
    if (!divRef.current) return;
    const contentHeight = divRef.current.scrollHeight;
    const nextHeight = Math.max(contentHeight, editStartHeight.current);
    if (Math.abs(nextHeight - element.size.height) > 0.5) {
      dispatch(
        updateElement({
          id: element.id,
          size: { width: element.size.width, height: nextHeight },
        })
      );
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when editing starts, not on every element/content identity change
  useEffect(() => {
    if (isEditing) {
      editStartHeight.current = element.size.height;
      // Also covers opening edit mode on an existing element whose saved height is already too
      // short for its (possibly multiline) content — e.g. content authored elsewhere.
      syncHeightToContent();
    }
  }, [isEditing]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // seconds
  const [duration, setDuration] = useState(0);

  const audioSrc = element.audio?.src ?? null;
  const hasAudio = !!audioSrc;
  const displayMode = element.audioDisplayMode ?? "text";
  const boundaries = element.audioWordBoundaries ?? [];

  // Uncontrolled div — React must not own the child text node, or editing throws NotFoundError.
  useLayoutEffect(() => {
    if (divRef.current && !isFocused.current && divRef.current.innerText !== element.content) {
      divRef.current.innerText = element.content;
    }
  }, [element.content]);

  // Focus + place the caret at the click point (double-click = whole word), else at the end.
  // biome-ignore lint/correctness/useExhaustiveDependencies: caretNonce is a deliberate re-run trigger
  useEffect(() => {
    if (!isEditing || !isSelected || !divRef.current) return;
    const el = divRef.current;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;

    const pt = editCaretPoint.current;
    const selectWord = editSelectWord.current;
    editCaretPoint.current = null;
    editSelectWord.current = false;
    try {
      let caret: { node: Node; offset: number } | null = null;
      if (pt) {
        const fromRange = (
          document as Document & {
            caretRangeFromPoint?: (x: number, y: number) => Range | null;
          }
        ).caretRangeFromPoint;
        const fromPos = (
          document as Document & {
            caretPositionFromPoint?: (
              x: number,
              y: number
            ) => { offsetNode: Node; offset: number } | null;
          }
        ).caretPositionFromPoint;
        if (fromRange) {
          const r = fromRange(pt.x, pt.y);
          if (r) caret = { node: r.startContainer, offset: r.startOffset };
        } else if (fromPos) {
          const p = fromPos(pt.x, pt.y);
          if (p) caret = { node: p.offsetNode, offset: p.offset };
        }
        if (caret && !el.contains(caret.node)) caret = null;
      }
      if (caret) {
        sel.collapse(caret.node, caret.offset);
        if (selectWord && typeof sel.modify === "function") {
          sel.modify("move", "backward", "word");
          sel.modify("extend", "forward", "word");
        }
      } else {
        sel.selectAllChildren(el);
        sel.collapseToEnd();
      }
    } catch {
      // Caret placement is a nicety; never let it break entering edit mode.
    }
  }, [isEditing, isSelected, caretNonce]);

  // Moveable preventDefaults its own mousedown, so a press on the frame won't blur the box —
  // this capture listener blurs it, and the same gesture still starts the drag.
  useEffect(() => {
    if (!isEditing) return;
    const onDocPointerDown = (e: PointerEvent) => {
      if (divRef.current && !divRef.current.contains(e.target as Node)) {
        divRef.current.blur();
      }
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [isEditing]);

  // Own the audio element for in-element playback (player mode + text-mode highlight). Recreated
  // whenever the source changes (e.g. after regenerating), and torn down on unmount.
  useEffect(() => {
    if (!audioSrc) {
      audioRef.current = null;
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      return;
    }
    const audio = new Audio(audioSrc);
    audioRef.current = audio;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration || 0);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audioRef.current = null;
    };
  }, [audioSrc]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  // Which word is currently spoken: the last boundary whose start offset (ms) has been reached.
  const activeWordIndex = useMemo(() => {
    if (!isPlaying || boundaries.length === 0) return -1;
    const ms = currentTime * 1000;
    let idx = -1;
    for (let i = 0; i < boundaries.length; i++) {
      const boundary = boundaries[i];
      if (boundary && boundary.offset <= ms) idx = i;
      else break;
    }
    return idx;
  }, [isPlaying, currentTime, boundaries]);

  const textStyle: React.CSSProperties = {
    fontSize: element.style.fontSize,
    // Boldness is baked into the chosen font FILE (resolveCanvasFontFamily), not this property —
    // also passing "bold" here would ask the browser to synthesize extra weight on top of an
    // already-bold face.
    fontWeight: "normal",
    color: element.style.color,
    fontFamily: resolveCanvasFontFamily(element.style.fontFamily, element.style.fontWeight),
    textAlign: (element.style.textAlign || "center") as React.CSSProperties["textAlign"],
    textDecoration: element.style.textDecoration || "none",
    fontStyle: element.style.fontStyle || "normal",
    padding: "4px",
    lineHeight: 1,
    whiteSpace: "pre-wrap",
    borderRadius: element.border.cornerRadius - element.border.strokeWidth - 3,
    ...(element.backgroundColor ? { backgroundColor: element.backgroundColor } : {}),
  };

  // ── Player display mode: compact play button + progress bar, no text ──────────────────────────
  if (hasAudio && displayMode === "player") {
    const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      audio.currentTime = fraction * duration;
    };

    return (
      <div
        className="w-full h-full flex items-center gap-3 px-3"
        style={{
          borderRadius: element.border.cornerRadius - element.border.strokeWidth - 3,
          backgroundColor: element.backgroundColor || "#f8fafc",
        }}
      >
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
          className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-500 text-white flex items-center justify-center hover:bg-primary-600 transition-colors"
        >
          {isPlaying ? (
            <FaPause className="w-3.5 h-3.5" />
          ) : (
            <FaPlay className="w-3.5 h-3.5 ml-0.5" />
          )}
        </button>
        <div
          className="flex-1 h-2 bg-gray-200 rounded-full cursor-pointer overflow-hidden"
          onClick={handleSeek}
        >
          <div
            className="h-full bg-primary-500 rounded-full"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
    );
  }

  // ── Text mode with live highlight (only while playing, and only if we have per-word timings) ───
  const showHighlighted = hasAudio && boundaries.length > 0 && isPlaying;
  if (showHighlighted) {
    // Split into word + whitespace tokens so spacing/wrapping is preserved; only word tokens map to
    // boundary indices (boundaries were captured per spoken word).
    const tokens = element.content.split(/(\s+)/);
    let wordCounter = -1;
    return (
      <div
        className="w-full h-full outline-none overflow-hidden flex flex-col justify-center relative"
        style={textStyle}
      >
        <div>
          {tokens.map((token, i) => {
            if (/^\s+$/.test(token) || token === "") {
              // biome-ignore lint/suspicious/noArrayIndexKey: static token list, index is stable
              return <span key={i}>{token}</span>;
            }
            wordCounter++;
            const isActive = wordCounter === activeWordIndex;
            return (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: static token list, index is stable
                key={i}
                style={
                  isActive
                    ? { backgroundColor: "#fde047", borderRadius: 4, padding: "0 2px" }
                    : undefined
                }
              >
                {token}
              </span>
            );
          })}
        </div>
        <PlayButton isPlaying={isPlaying} onToggle={togglePlay} />
      </div>
    );
  }

  // ── Default: editable text (editor) / plain text (live), with a small play button when audio exists ─
  // The vertical-centering flex box must live on a WRAPPER, not the contentEditable element
  // itself — Chrome silently swallows the Enter key inside a contentEditable that has
  // `display: flex` directly on it (it can't split flex-item content into a new line), so no
  // newline was ever created. The actual editable node stays a plain block.
  return (
    <div className="relative w-full h-full">
      <div
        // Only while editing does a press on the text skip Moveable (to place the caret).
        data-no-moveable-drag={!isLiveMode && isEditing ? "" : undefined}
        className={`w-full h-full overflow-hidden flex flex-col justify-center ${
          isEditing ? "cursor-text" : "cursor-move"
        } ${isEditing ? "" : "select-none"}`}
        style={{
          borderRadius: textStyle.borderRadius,
          backgroundColor: textStyle.backgroundColor,
        }}
        onPointerDown={(e) => {
          pointerDownAt.current = { x: e.clientX, y: e.clientY };
        }}
        onClick={(e) => {
          if (isLiveMode || isEditing || !isSelected) return;
          const down = pointerDownAt.current;
          const moved = !!down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 4;
          if (!moved) {
            editCaretPoint.current = { x: e.clientX, y: e.clientY };
            setCaretNonce((n) => n + 1);
            setIsEditing(true);
          }
        }}
        onDoubleClick={
          isLiveMode
            ? undefined
            : (e) => {
                editCaretPoint.current = { x: e.clientX, y: e.clientY };
                editSelectWord.current = true;
                setCaretNonce((n) => n + 1);
                setIsEditing(true);
              }
        }
      >
        <div
          ref={divRef}
          contentEditable={!isLiveMode && isEditing}
          suppressContentEditableWarning
          className="w-full outline-none selection:bg-blue-500 selection:text-white"
          style={{ ...textStyle, borderRadius: undefined, backgroundColor: undefined }}
          onFocus={() => {
            isFocused.current = true;
          }}
          onInput={!isLiveMode ? syncHeightToContent : undefined}
          onBlur={
            !isLiveMode
              ? (e) => {
                  isFocused.current = false;
                  setIsEditing(false);
                  dispatch(
                    updateElementProps({
                      id: element.id,
                      props: {
                        content: e.target.innerText,
                      },
                    })
                  );
                }
              : undefined
          }
          onKeyDown={
            !isLiveMode
              ? (e) => {
                  // Tab/Escape leave edit mode; Tab must not indent the contentEditable first.
                  if (e.key === "Tab" || e.key === "Escape") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }
              : undefined
          }
        />
      </div>
      {hasAudio && <PlayButton isPlaying={isPlaying} onToggle={togglePlay} />}
    </div>
  );
}

interface PlayButtonProps {
  isPlaying: boolean;
  onToggle: () => void;
}

/** Small floating play/pause control shown on a text element that has audio. */
function PlayButton({ isPlaying, onToggle }: PlayButtonProps) {
  return (
    <button
      type="button"
      // Prevent the click from focusing the contentEditable / starting a drag.
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-label={isPlaying ? "Pause" : "Play"}
      className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-primary-500/90 text-white flex items-center justify-center shadow hover:bg-primary-600 transition-colors z-10"
    >
      {isPlaying ? <FaPause className="w-2.5 h-2.5" /> : <FaPlay className="w-2.5 h-2.5 ml-0.5" />}
    </button>
  );
}
